// ABOUTME: React hooks over the sponsored-link service: the full list for the
// ABOUTME: admin panel, and one weighted pick that holds steady for the session.

import { useEffect, useState } from "react";
import { useNow } from "./useNow";
import { adService } from "../services/adService";
import { isAdLive, pickWeightedAd, type AdRecord } from "../utils/ads";

/** The ad shown on the previous page load, so the next roll varies. */
export const LAST_AD_KEY = "ad:last";

function readLastAdId(): string | null {
  try {
    return localStorage.getItem(LAST_AD_KEY);
  } catch {
    return null;
  }
}

function writeLastAdId(id: string | null): void {
  try {
    if (id) localStorage.setItem(LAST_AD_KEY, id);
    else localStorage.removeItem(LAST_AD_KEY);
  } catch {
    // localStorage may be unavailable; rotation just loses its memory.
  }
}

/** Every ad document, live or not. Used by the admin panel. */
export function useAds(): AdRecord[] {
  const [ads, setAds] = useState<AdRecord[]>([]);
  useEffect(() => adService.subscribeToAds(setAds), []);
  return ads;
}

/**
 * One live ad for the dashboard, chosen by weight.
 *
 * The pick is made once and then kept as long as that ad stays live, so an
 * admin editing an unrelated ad (which re-fires the snapshot) doesn't swap
 * the card under the user. An ad whose flight ends mid-session is replaced
 * within a minute. A fresh page load is a fresh roll — that is the
 * rotation — and the previous load's pick sits that roll out when there is
 * more than one live ad. Returns null while loading, when nothing is live,
 * or when `enabled` is false (ad-free tiers).
 */
export function useActiveAd(enabled = true): AdRecord | null {
  const ads = useAds();
  const now = useNow();
  // One roll and one memory of the previous load per mount, so the pick is a
  // pure function of the ads and the clock.
  const [roll] = useState(Math.random);
  const [lastLoadId] = useState(readLastAdId);
  const [heldId, setHeldId] = useState<string | null>(null);

  const held = heldId ? ads.find((a) => a.id === heldId) : undefined;
  let active: AdRecord | null = null;
  if (enabled) {
    active = held && isAdLive(held, now) ? held : pickWeightedAd(ads, now, () => roll, lastLoadId);
  }
  const activeId = active?.id ?? null;
  // Remember the pick so a later snapshot keeps it. Updating state while
  // rendering is React's pattern for deriving from the previous render.
  if (enabled && activeId !== heldId) setHeldId(activeId);

  useEffect(() => {
    if (activeId) writeLastAdId(activeId);
  }, [activeId]);

  return active;
}
